import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import { useContextualRouting } from "next-use-contextual-routing";
import useRedirect from "@shared/hooks/useRedirect.web";
import { useEffect } from "react";
import { useQueryClient } from "react-query";
import WebviewComponentAdaptor from "@shared/components/application/other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";
import { FavoriteHeartButtonProps } from "./FavoriteHeartButton.props";
import { useFavoriteHeartButton } from "./useFavoriteHeartButton";
import UserBasedFavoriteEventRepository from "../../data/repositories/UserBasedFavoriteEventRepository";
import { UserBasedFavoriteEventSerializer } from "../../data/types/favorite-events";

const favoriteRepo = new UserBasedFavoriteEventRepository();

const FavoriteHeartButton = ({
  favoriteData = undefined,
  isAbsolute = true,
  transparent = false,
  showHoverState = false,
  containerSize,
}: FavoriteHeartButtonProps) => {
  const { redirect } = useRedirect();
  const queryClient = useQueryClient();
  const { makeContextualHref } = useContextualRouting();

  const {
    displayRight,
    isLoading,
    isLoggedIn,
    extractedEventId,
    favoriteEventIds,
    favoriteEvents,
    addFavoriteEventMutation,
    removeFavoriteEventMutation,
  } = useFavoriteHeartButton(favoriteData);

  useEffect(() => {
    if (isLoggedIn === false) {
      const repoName = favoriteRepo.getRepositoryName();
      queryClient.setQueriesData([repoName], null);
      queryClient.removeQueries([repoName]);
    }
  }, [isLoggedIn, queryClient]);

  const handleFavorite = (eventId: number | null) => {
    if (!isLoggedIn) {
      redirect(makeContextualHref({ accountModal: "login" }), {
        as: "/accounts/login/",
        shallow: true,
      });
      return;
    }

    if (eventId === null) {
      return;
    }

    // add favorite
    if (!favoriteEventIds?.includes(eventId)) {
      addFavoriteEventMutation.mutate({
        event_id: eventId,
      } as UserBasedFavoriteEventSerializer);
    }
    // remove favorite
    else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let favoritedEventRecord: any;

      favoriteEvents?.pages?.forEach((page) => {
        const matchedEvent = page?.results?.find(
          (favoritedEvent) => favoritedEvent?.event?.id === eventId
        );
        if (matchedEvent) {
          favoritedEventRecord = matchedEvent;
        }
      });

      removeFavoriteEventMutation.mutate(favoritedEventRecord?.id);
    }
  };

  return extractedEventId ? (
    <WebviewComponentAdaptor
      webComponent={
        <Box data-testid="card-image-heart-container-box">
          <Box
            cursor={isLoading ? "wait" : "pointer"}
            position={isAbsolute ? "absolute" : "relative"}
            zIndex={isAbsolute ? "2" : undefined}
            display="flex"
            alignItems="center"
            justifyContent="center"
            backgroundColor={transparent ? "transparent" : "bg/40"}
            borderRadius="sm"
            top={isAbsolute ? "xs" : undefined}
            height={containerSize ?? "element.2xl"}
            width={containerSize ?? "element.2xl"}
            minHeight={containerSize}
            minWidth={containerSize}
            right={isAbsolute && displayRight ? "10px" : undefined}
            left={isAbsolute && !displayRight ? "10px" : undefined}
            transition="background-color 0.2s"
            _hover={
              showHoverState
                ? {
                    _light: { bg: transparent ? "whiteAlpha.400" : "gray.100" },
                    _dark: { bg: "whiteAlpha.200" },
                  }
                : undefined
            }
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (isLoading) {
                return;
              }
              handleFavorite(extractedEventId);
            }}
          >
            {favoriteEventIds?.includes(extractedEventId) ? (
              <Icon.FavoriteHeart
                outline={false}
                aria-hidden="false"
                color="brandSecondary.600"
                height="auto"
                width={
                  transparent
                    ? favoriteData?.customIconSize ?? "element.xl"
                    : {
                        sm: favoriteData?.customIconSize ?? "element.xl",
                        base: favoriteData?.customIconSize ?? "element.sm",
                      }
                }
              />
            ) : (
              <Icon.FavoriteHeart
                outline
                aria-hidden="false"
                color={{
                  _light: transparent ? "fg" : "brandSecondary.600",
                  _dark: "fg",
                }}
                height="auto"
                width={favoriteData?.customIconSize ?? "element.xl"}
              />
            )}
          </Box>
        </Box>
      }
      webviewComponent={null}
    />
  ) : (
    <></>
  );
};

export default FavoriteHeartButton;
